import { useMemo } from 'react';

const BG_ALPHABET = 'АБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЬЮЯ'.split('');
const EN_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const BG_RANK = new Map(BG_ALPHABET.map((ch, i) => [ch, i]));
const EN_RANK = new Map(EN_ALPHABET.map((ch, i) => [ch, i]));

const NAME_COLLATOR = new Intl.Collator(['bg-BG', 'en-US'], {
  usage: 'sort',
  sensitivity: 'base',
  numeric: true,
  ignorePunctuation: true
});

const segmenter =
  typeof Intl !== 'undefined' && Intl.Segmenter
    ? new Intl.Segmenter(['bg-BG', 'en-US'], { granularity: 'grapheme' })
    : null;

const safeText = v => (v?.title ?? v?.name ?? '').toString();

const getFirstMeaningfulGrapheme = text => {
  const raw = (text ?? '').toString().normalize('NFC').trim();
  if (!raw) return '';

  const graphemes = segmenter
    ? Array.from(segmenter.segment(raw), x => x.segment)
    : Array.from(raw);

  for (const g of graphemes) {
    if (/^\s+$/u.test(g)) continue;

    if (/^\p{L}$/u.test(g) || /^\p{N}$/u.test(g)) return g;
  }

  return '';
};

const isDigit = ch => /^\p{N}$/u.test(ch);
const isCyrillic = ch => /[\u0400-\u04FF]/.test(ch);
const isLatin = ch => /[A-Za-z]/.test(ch);

const normalizeLetter = text => {
  const first = getFirstMeaningfulGrapheme(text);
  if (!first) return '#';

  if (isDigit(first)) return '#';

  if (isCyrillic(first)) return first.toLocaleUpperCase('bg-BG');
  if (isLatin(first)) return first.toLocaleUpperCase('en-US');

  return first.toLocaleUpperCase();
};

const letterOrderKey = letter => {
  if (letter === '#') return { bucket: 0, rank: 0 };
  if (BG_RANK.has(letter)) return { bucket: 1, rank: BG_RANK.get(letter) };
  if (EN_RANK.has(letter)) return { bucket: 2, rank: EN_RANK.get(letter) };
  return { bucket: 3, rank: 0 };
};

const compareLetters = (a, b) => {
  if (a === b) return 0;
  const ka = letterOrderKey(a);
  const kb = letterOrderKey(b);

  if (ka.bucket !== kb.bucket) return ka.bucket - kb.bucket;
  if (ka.rank !== kb.rank) return ka.rank - kb.rank;

  return NAME_COLLATOR.compare(a, b);
};

const useSplitChunks = (contentSubpages, deps = [], debug = false) =>
  useMemo(() => {
    if (!Array.isArray(contentSubpages) || contentSubpages.length === 0)
      return [];

    const log = (...args) => {
      if (debug) console.log('[useSplitChunks]', ...args);
    };

    if (debug) {
      log('INPUT size:', contentSubpages.length);
      for (let i = 0; i < Math.min(contentSubpages.length, 50); i++) {
        const text = safeText(contentSubpages[i]);
        const first = getFirstMeaningfulGrapheme(text);
        const letter = normalizeLetter(text);
        log(`INPUT[${i}]`, {
          id: contentSubpages[i]?.id,
          text,
          firstMeaningful: first,
          firstCodePointHex: first ? first.codePointAt(0).toString(16) : null,
          normalizedLetter: letter
        });
      }
    }

    const sorted = [...contentSubpages].sort((a, b) => {
      const ta = safeText(a);
      const tb = safeText(b);

      const la = normalizeLetter(ta);
      const lb = normalizeLetter(tb);

      const byLetter = compareLetters(la, lb);
      if (byLetter !== 0) return byLetter;

      const byText = NAME_COLLATOR.compare(ta, tb);
      if (byText !== 0) return byText;

      return Number(a?.id ?? 0) - Number(b?.id ?? 0);
    });

    const build = [];
    for (const item of sorted) {
      const text = safeText(item);
      const letter = normalizeLetter(text);

      const last = build[build.length - 1];
      if (!last || last.letter !== letter) {
        build.push({ letter, content: [item] });
      } else {
        last.content.push(item);
      }

      if (debug) {
        log('ASSIGN', {
          id: item?.id,
          text,
          letter
        });
      }
    }

    if (debug) {
      log(
        'GROUPS:',
        build.map(g => ({
          letter: g.letter,
          count: g.content.length,
          first: safeText(g.content[0]),
          last: safeText(g.content[g.content.length - 1])
        }))
      );
    }

    return build;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentSubpages, debug, ...deps]);

export default useSplitChunks;
